import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { message } from "antd";
import request from "../utils/request";
import {
  normalizeV3Project,
  normalizeV3User,
  type V3ProjectDetail,
  type V3InviteInfo,
  type V3ProjectListItem,
  type V3UserProfile,
} from "../utils/v3Compat";

// JWTPayload 接口
interface JWTPayload {
  openid: string;
  iss: string;
  exp: number;
  iat: number;
}

// VIP信息接口
interface VipInfo {
  is_vip: boolean;
  start_time: string;
  end_time: string;
  level: number;
}

// PostChat信息接口
interface PostchatInfo {
  api_key: string;
  vip_info: VipInfo;
  token: number;
}

// OAuth信息接口
interface OAuthInfo {
  qq_bind: boolean;
  github_bind: boolean;
}

// 添加邀请信息接口
interface InviteInfo {
  is_invited: boolean;
  invite_reward_valid: boolean;
  invited_at: string;
  invite_code?: string;
  invite_count?: number;
  reward_total?: number;
  invite_remaining_days: number;
}

// API返回的用户信息接口
interface UserApiInfo {
  open_id: number;
  email: string;
  postchat: PostchatInfo;
  oauth_info: OAuthInfo;
  role: string;
  invite: InviteInfo;
}

// 定义项目类型
export interface Project {
  title: string;
  key: string;
  apiKey: string;
  type: "postchat" | "summary";
  websites?: string[];
  summaryUrlBinding?: boolean;
  summaryLanguage?: "中文" | "多国语言";
  tokenBalance?: number;
  services?: string[];
  knowledgeBaseCount?: number;
  expiryDate?: string;
  remainingDays?: number;
}

// 更新用户类型以包含 API 信息
export interface User {
  uid: string;
  avatar: string;
  memberStatus: "PostChat会员" | "普通用户";
  expiryDate?: string;
  remainingDays?: number;
  apiInfo?: UserApiInfo;
}

// 知识库条目接口
export interface KnowledgeItem {
  id: number;
  title: string;
  created_at: string;
  url?: string;
  project?: string;
  project_id?: number;
  source?: string;
  website?: string;
  content?: string;
  summary?: string;
  token_count?: number;
  refer?: string;
  is_vector?: boolean;
  doc_type?: string;
}

// 项目统计信息接口
interface ProjectStats {
  token_used: number;
  use_count: number;
  summary_count: number;
  knowledge_count: number;
}

// 总体统计信息接口
interface TotalStats {
  token_used: number;
  use_count: number;
}

// 项目列表响应数据接口
interface V3ProjectListResponse {
  total: number;
  list: V3ProjectListItem[];
  total_stats: TotalStats;
  max_postchat: number;
}

interface V3ProjectDetailResponse {
  project?: {
    config?: Record<string, unknown>;
  };
}

interface ApiResponse<T> {
  code: number;
  msg: string;
  data: T;
}

// 单个项目信息接口
export interface ProjectItem {
  id: number;
  name: string;
  key_value: string;
  key_value_list: string[];
  api_key: string;
  refer: string;
  token: number;
  url_binding: boolean;
  created_at: string;
  multi_language: boolean;
  is_postchat: boolean;
  is_public: boolean;
  stats: ProjectStats;
  prompt: string;
  enable_mcp: boolean;
  enable_podcast: boolean | number; // API返回boolean，但有些地方可能返回number(1/0)
  // 项目级语言策略（全局生效：摘要/对话/热词/推荐/播客/Agent）
  language_mode?: string; // "follow" | "target"
  default_language?: string; // 缺省回落语言，默认 zh-CN
  target_language?: string; // mode=target 时强制语言
}

export interface RechargeProduct {
  id: number;
  code: string;
  type: string;
  vip_level?: number;
  name: string;
  price?: number;
  token_amount?: number;
  original_price?: number;
  discount_desc?: string;
  discount_rate?: number;
  discount_type?: string;
  discount_start?: string;
  discount_end?: string;
  reward_product?: string;
  description?: string;
}

// 更新全局状态类型
interface GlobalContextType {
  user: User;
  projects: ProjectItem[];
  notices: { id: number; title: string }[];
  setUser: (user: User) => void;
  setProjects: (projects: ProjectItem[]) => void;
  updateProject: (projectTitle: string, updates: Partial<Project>) => void;
  API_BASE_URL: string;
  token: string | null;
  setToken: (token: string | null) => void;
  loading: boolean;
  error: string | null;
  refreshUserInfo: () => Promise<void>;
  refreshProjects: () => Promise<void>;
}

// 创建 Context
const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

const configuredAPIBaseURL = process.env.REACT_APP_API_BASE_URL?.trim();
const API_BASE_URL =
  configuredAPIBaseURL === "__SAME_ORIGIN__"
    ? window.location.origin
    : configuredAPIBaseURL || "https://api.ai.zhheo.com";

// 创建 Provider 组件
export const GlobalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User>({
    uid: "128",
    avatar: "/assets/images/hongmoai-logo-white",
    memberStatus: "PostChat会员",
    expiryDate: "2026/2/7",
    remainingDays: 363,
  });

  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [notices, setNotices] = useState<{ id: number; title: string }[]>([]);
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem("jwt_token")
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initRef = React.useRef(false);
  // 添加token刷新状态
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);

  // token 存储效果
  useEffect(() => {
    if (token) {
      localStorage.setItem("jwt_token", token);
    } else {
      localStorage.removeItem("jwt_token");
      initRef.current = false;
    }
  }, [token]);

  // 检查token是否即将过期
  const checkTokenExpiration = useCallback((token: string): number => {
    try {
      const decoded = jwtDecode<JWTPayload>(token);
      const currentTime = Math.floor(Date.now() / 1000);
      // 返回剩余秒数
      return decoded.exp - currentTime;
    } catch (error) {
      return -1; // 解析失败，返回-1表示过期
    }
  }, []);

  // 刷新token函数
  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (!token || isRefreshingToken) return false;

    setIsRefreshingToken(true);
    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/auth/refresh`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200 && response.data.data?.token) {
        console.log("Token刷新成功");
        setToken(response.data.data.token);
        setIsRefreshingToken(false);
        return true;
      } else {
        console.warn("Token刷新失败:", response.data.msg);
        setIsRefreshingToken(false);
        return false;
      }
    } catch (error) {
      console.error("Token刷新出错:", error);
      setIsRefreshingToken(false);
      return false;
    }
  }, [token, isRefreshingToken]);

  // 定期检查token是否即将过期
  useEffect(() => {
    if (!token) return;

    const checkInterval = setInterval(() => {
      const remainingTime = checkTokenExpiration(token);

      // 如果剩余有效期小于30分钟(1800秒)但大于5分钟(300秒)，尝试刷新token
      if (remainingTime > 300 && remainingTime < 1800) {
        console.log(`Token将在${remainingTime}秒后过期，尝试刷新...`);
        refreshToken().catch((err) => {
          console.error("自动刷新token失败:", err);
        });
      }
      // 如果剩余时间不足5分钟，显示提醒
      else if (remainingTime > 0 && remainingTime <= 300) {
        message.warning({
          content: "您的登录状态即将过期，建议刷新页面",
          key: "token-expiring",
          duration: 10,
        });
      }
    }, 5 * 60 * 1000); // 每5分钟检查一次

    return () => {
      clearInterval(checkInterval);
    };
  }, [token, checkTokenExpiration, refreshToken]);

  // 使用 useCallback 包装 fetchUserInfo
  const fetchUserInfo = useCallback(
    async (signal?: AbortSignal) => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const [profileResult, inviteResult] = await Promise.allSettled([
          request.get<ApiResponse<V3UserProfile>>(
            `${API_BASE_URL}/api/v3/user/profile`,
            {
              signal,
            }
          ),
          request.get<ApiResponse<V3InviteInfo>>(
            `${API_BASE_URL}/api/v3/invite`,
            {
              signal,
            }
          ),
        ]);

        if (profileResult.status !== "fulfilled") {
          throw profileResult.reason;
        }

        if (profileResult.value.data.code === 200) {
          const inviteInfo =
            inviteResult.status === "fulfilled" &&
            inviteResult.value.data.code === 200
              ? inviteResult.value.data.data
              : null;

          setUser((prevUser) => ({
            ...prevUser,
            ...normalizeV3User(profileResult.value.data.data, null, inviteInfo),
          }));
        } else {
          setError(profileResult.value.data.msg);
          console.warn(
            "获取用户信息接口返回非200状态码:",
            profileResult.value.data.msg
          );
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          const axiosError = err as any;
          // 只有在401错误时才清除token
          if (axiosError.response?.status === 401) {
            console.error("授权已过期，需要重新登录");
            // 这里不直接清除token，让request拦截器处理
          } else {
            // 其他错误只记录，不影响用户体验
            console.error("获取用户信息失败:", axiosError);
            setError("获取用户信息失败，但不影响基本使用");
          }
        }
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  // 使用 useCallback 包装 fetchProjects
  const fetchProjects = useCallback(
    async (signal?: AbortSignal, retryCount = 0) => {
      if (!token) return;

      try {
        const response = await request.get<ApiResponse<V3ProjectListResponse>>(
          `${API_BASE_URL}/api/v3/projects`,
          {
            signal,
          }
        );

        if (response.data.code === 200) {
          const projectList = response.data.data.list || [];
          const detailPairs = await Promise.all(
            projectList.map(async (project) => {
              try {
                const detailResponse = await request.get<
                  ApiResponse<V3ProjectDetailResponse>
                >(`${API_BASE_URL}/api/v3/projects/${project.id}`, {
                  signal,
                });

                if (detailResponse.data.code === 200) {
                  return [project.id, detailResponse.data.data as V3ProjectDetail] as const;
                }
              } catch (detailError) {
                if (!axios.isCancel(detailError)) {
                  console.warn("获取项目详情失败:", project.id, detailError);
                }
              }

              return [project.id, null] as const;
            })
          );

          const detailMap = new Map(detailPairs);
          setProjects(
            projectList.map((project) =>
              normalizeV3Project(project, detailMap.get(project.id) || null)
            )
          );
        } else {
          // 非严重错误，不清除token
          console.warn("获取项目列表接口返回非200状态码:", response.data.msg);
          setError(response.data.msg);
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          const axiosError = err as any;
          // 只有在严重错误时才设置错误状态，但不影响token
          if (axiosError.response?.status !== 401) {
            console.error("获取项目列表失败:", axiosError);
            setError("获取项目列表失败，但不影响基本使用");
          }

          // 如果是网络错误，尝试重试一次
          if (
            (!axiosError.response || axiosError.code === "ECONNABORTED") &&
            retryCount < 2
          ) {
            console.log(
              `获取项目列表失败，1秒后进行第 ${retryCount + 1} 次重试...`
            );
            setTimeout(() => {
              fetchProjects(signal, retryCount + 1);
            }, 1000);
          }
        }
      }
    },
    [token]
  );

  // 使用 useCallback 包装 fetchNotices
  const fetchNotices = useCallback(
    async (signal?: AbortSignal, retryCount = 0) => {
      if (!token) return;

      try {
        const response = await request.get(`${API_BASE_URL}/api/v3/notices`, {
          params: {
            page: 1,
            page_size: 3,
            hidden: 0,
          },
          signal,
        });

        if (response.data.code === 200) {
          setNotices(
            response.data.data.list.map((item: any) => ({
              id: item.id,
              title: item.title,
            }))
          );
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          const axiosError = error as any;
          console.error("获取公告失败:", axiosError);

          // 如果是网络错误，尝试重试
          if (
            (!axiosError.response || axiosError.code === "ECONNABORTED") &&
            retryCount < 2
          ) {
            console.log(
              `获取公告失败，1秒后进行第 ${retryCount + 1} 次重试...`
            );
            setTimeout(() => {
              fetchNotices(signal, retryCount + 1);
            }, 1000);
          }
        }
      }
    },
    [token]
  );

  // 修改 useEffect，使用 ref 防止重复初始化
  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();

    const initializeData = async () => {
      if (!mounted) return;

      if (token && !initRef.current) {
        initRef.current = true;
        setLoading(true);
        try {
          // 分开执行Promise，让一个API失败不影响其他API
          try {
            await fetchUserInfo(controller.signal);
          } catch (error) {
            console.error("初始化用户数据失败:", error);
          }

          try {
            await fetchProjects(controller.signal);
          } catch (error) {
            console.error("初始化项目数据失败:", error);
          }

          try {
            await fetchNotices(controller.signal);
          } catch (error) {
            console.error("初始化公告数据失败:", error);
          }
        } catch (error) {
          if (!axios.isCancel(error)) {
            console.error("初始化数据失败:", error);
          }
        } finally {
          if (mounted) {
            setLoading(false);
          }
        }
      } else if (!token) {
        setLoading(false);
      }
    };

    // 使用 setTimeout 来确保在组件稳定后再初始化
    const timeoutId = setTimeout(() => {
      initializeData();
    }, 0);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [token, fetchUserInfo, fetchProjects, fetchNotices]);

  // 更新单个项目的方法
  const updateProject = (projectTitle: string, updates: Partial<Project>) => {
    setProjects((prevProjects) =>
      prevProjects.map((project) =>
        project.name === projectTitle ? { ...project, ...updates } : project
      )
    );
  };

  const value = {
    user,
    projects,
    notices,
    setUser,
    setProjects,
    updateProject,
    API_BASE_URL,
    token,
    setToken,
    loading,
    error,
    refreshUserInfo: fetchUserInfo,
    refreshProjects: fetchProjects,
  };

  return (
    <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>
  );
};

// 创建自定义 hook 来使用 context
export const useGlobal = () => {
  const context = useContext(GlobalContext);
  if (context === undefined) {
    throw new Error("useGlobal must be used within a GlobalProvider");
  }
  return context;
};
